import { graphql } from "api/cms-website/generated";

import { AwardsFragment } from "./fragments/components/awards";
import { ClinicsListFragment } from "./fragments/components/clinicsList";
import { ClinicsMapFragment } from "./fragments/components/clinicsMap";
import { MainBannerFragment } from "./fragments/components/mainBanner";
import { MainBannerHeaderFragment } from "./fragments/components/mainBannerHeader";
import { MainBannerExtraFragment } from "./fragments/components/mainBannerExtra";
import { TwoColumnListFragment } from "./fragments/components/twoColumnList";
import { TextWithCTAFragment } from "./fragments/components/textWithCTA";
import { TextWithCTAFiftyFiftyFragment } from "./fragments/components/textWithCTAFiftyFifty";
import { AdvantagesWithHeadingFragment } from "./fragments/components/advantagesWithHeading";
import { MultiColumnSliderFragment } from "./fragments/components/multiColumnSlider";
import { QuestionsWithImageFragment } from "./fragments/components/questionsWithImage";
import { MapFragment } from "./fragments/components/map";
import { QuestionsFragment } from "./fragments/components/questions";
import { SliderFragment } from "./fragments/components/slider";
import { LargePictureFragment } from "./fragments/components/largePicture";
import { PriceBlockFragment } from "./fragments/components/priceBlock";
import { LeadFormFragment } from "./fragments/components/leadForm";
import { OpenGraphFragment, SeoFragment } from "./fragments/seo";
import { TreatmentInfoFragment } from "./fragments/components/treatmentInfo";
import { MultiColumnFragment } from "./fragments/components/multiColumn";
import { VerticalAdvantagesFragment } from "./fragments/components/verticalAdvantages";
import { HalfTextAndImageBannerFragment } from "./fragments/components/halfTextAndImageBanner";
import { VerticalListWithImageFragment } from "./fragments/components/verticalListWithImage";
import { VerticalListSectionFragment } from "./fragments/components/verticalListSection";
import { TreatmentIncludesSectionFragment } from "./fragments/components/treatmentIncludesSection";
import { IllustratedListFragment } from "./fragments/components/illustratedList";
import { TabbedContentFragment } from "./fragments/components/tabbedContent";
import { TableWithCheckmarksFragment } from "./fragments/components/tableWithCheckmarks";
import { BannerPaidSocialFragment } from "./fragments/components/bannerPaidSocial";
import { InfoBannerFragment } from "./fragments/components/infoBanner";
import { ImageCardGridFragment } from "./fragments/components/imageCardGrid";
import { ListWithCTAFragment } from "./fragments/components/listWithCTA";
import { FollowUsFragment } from "./fragments/components/followUs";
import { VideoFragment } from "./fragments/components/video";
import { ReferralBannerFragment } from "./fragments/components/referralBanner";
import { ReferralHeroFragment } from "./fragments/components/referralHero";
import { TermLinkFragment } from "./fragments/components/termLink";

/** Default pagination for page components relation (Strapi limits to 10 without it). */
export const PAGE_COMPONENTS_PAGINATION = { limit: 100 } as const;

export const PageFragment = graphql(/* GraphQL */ `
    fragment PageFragment on Page {
        documentId
        slug
        locale
        name
        noFooterMarginTop
        withHeader
        withFooter
        darkMode
        hideMenu
        seo {
            ...SeoFragment
        }
        components(pagination: $componentsPagination) {
            documentId
            selected {
                __typename
                ... on ComponentComponentsAwards {
                    ...AwardsFragment
                }
                ... on ComponentComponentsClinicsList {
                    ...ClinicsListFragment
                }
                ... on ComponentComponentsClinicsMap {
                    ...ClinicsMapFragment
                }
                ... on ComponentComponentsMainBanner {
                    ...MainBannerFragment
                }
                ... on ComponentComponentsMainBannerHeader {
                    ...MainBannerHeaderFragment
                }
                ... on ComponentComponentsMainBannerExtra {
                    ...MainBannerExtraFragment
                }
                ... on ComponentComponentsTwoColumnList {
                    ...TwoColumnListFragment
                }
                ... on ComponentComponentsTextWithCta {
                    ...TextWithCTAFragment
                }
                ... on ComponentComponentsTextWithCtaFiftyFifty {
                    ...TextWithCTAFiftyFiftyFragment
                }
                ... on ComponentComponentsAdvantagesWithHeading {
                    ...AdvantagesWithHeadingFragment
                }
                ... on ComponentComponentsMultiColumnSlider {
                    ...MultiColumnSliderFragment
                }
                ... on ComponentComponentsQuestionsWithImage {
                    ...QuestionsWithImageFragment
                }
                ... on ComponentComponentsMap {
                    ...MapFragment
                }
                ... on ComponentComponentsQuestions {
                    ...QuestionsFragment
                }
                ... on ComponentComponentsLargePicture {
                    ...LargePictureFragment
                }
                ... on ComponentComponentsPriceBlock {
                    ...PriceBlockFragment
                }
                ... on ComponentComponentsLeadForm {
                    ...LeadFormFragment
                }
                ... on ComponentComponentsTreatmentInfo {
                    ...TreatmentInfoFragment
                }
                ... on ComponentComponentsMultiColumn {
                    ...MultiColumnFragment
                }
                ... on ComponentComponentsVerticalAdvantages {
                    ...VerticalAdvantagesFragment
                }
                ... on ComponentComponentsHalfTextAndImageBanner {
                    ...HalfTextAndImageBannerFragment
                }
                ... on ComponentComponentsVerticalListWithImage {
                    ...VerticalListWithImageFragment
                }
                ... on ComponentComponentsVerticalListSection {
                    ...VerticalListSectionFragment
                }
                ... on ComponentComponentsTreatmentIncludesSection {
                    ...TreatmentIncludesSectionFragment
                }
                ... on ComponentComponentsIllustratedList {
                    ...IllustratedListFragment
                }
                ... on ComponentComponentsTabbedContent {
                    ...TabbedContentFragment
                }
                ... on ComponentComponentsSlider {
                    ...SliderFragment
                }
                ... on ComponentComponentsTableWithCheckmarks {
                    ...TableWithCheckmarksFragment
                }
                ... on ComponentComponentsBannerPaidSocial {
                    ...BannerPaidSocialFragment
                }
                ... on ComponentComponentsInfoBanner {
                    ...InfoBannerFragment
                }
                ... on ComponentComponentsImageCardGrid {
                    ...ImageCardGridFragment
                }
                ... on ComponentComponentsListWithCta {
                    ...ListWithCTAFragment
                }
                ... on ComponentComponentsFollowUs {
                    ...FollowUsFragment
                }
                ... on ComponentComponentsVideo {
                    ...VideoFragment
                }
                ... on ComponentComponentsReferralBanner {
                    ...ReferralBannerFragment
                }
                ... on ComponentComponentsReferralHero {
                    ...ReferralHeroFragment
                }
                ... on ComponentComponentsTermLink {
                    ...TermLinkFragment
                }
            }
        }
        localizations {
            slug
            locale
        }
    }
`);

export const PageQuery = graphql(/* GraphQL */ `
    query PageQuery(
        $filters: PageFiltersInput
        $locale: I18NLocaleCode
        $status: PublicationStatus
        $componentsPagination: PaginationArg
    ) {
        pages(filters: $filters, locale: $locale, status: $status) {
            ...PageFragment
        }
    }
    ${PageFragment}
    ${SeoFragment}
    ${AwardsFragment}
    ${ClinicsListFragment}
    ${ClinicsMapFragment}
    ${OpenGraphFragment}
    ${MainBannerFragment}
    ${MainBannerHeaderFragment}
    ${MainBannerExtraFragment}
    ${TwoColumnListFragment}
    ${TextWithCTAFragment}
    ${TextWithCTAFiftyFiftyFragment}
    ${AdvantagesWithHeadingFragment}
    ${MultiColumnSliderFragment}
    ${QuestionsWithImageFragment}
    ${MapFragment}
    ${QuestionsFragment}
    ${LargePictureFragment}
    ${PriceBlockFragment}
    ${LeadFormFragment}
    ${TreatmentInfoFragment}
    ${MultiColumnFragment}
    ${VerticalAdvantagesFragment}
    ${HalfTextAndImageBannerFragment}
    ${VerticalListWithImageFragment}
    ${VerticalListSectionFragment}
    ${TreatmentIncludesSectionFragment}
    ${IllustratedListFragment}
    ${TabbedContentFragment}
    ${SliderFragment}
    ${TableWithCheckmarksFragment}
    ${BannerPaidSocialFragment}
    ${InfoBannerFragment}
    ${ImageCardGridFragment}
    ${ListWithCTAFragment}
    ${FollowUsFragment}
    ${VideoFragment}
    ${ReferralBannerFragment}
    ${ReferralHeroFragment}
    ${TermLinkFragment}
`);

// Query для получения всех страниц (для getPagesLocalizations)
export const PagesQuery = graphql(/* GraphQL */ `
    query PagesQuery(
        $filters: PageFiltersInput
        $locale: I18NLocaleCode
        $pagination: PaginationArg
        $status: PublicationStatus
    ) {
        pages(
            filters: $filters
            locale: $locale
            pagination: $pagination
            status: $status
        ) {
            documentId
            slug
            locale
            localizations {
                slug
                locale
            }
        }
    }
`);
